import api from '../../../api/client';
import React, { useState, useEffect } from "react";
import { Bar, Pie } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
} from "chart.js";
import config from "../../../config";
import axios from "axios";
import { Badge, Button, Card, Col, Row } from "react-bootstrap";
import MainCard from "../../../components/Card/MainCard";
import { useSelector } from "react-redux";
import { FaCalendarAlt } from "react-icons/fa";
import { Link } from "react-router-dom";

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
);

export default function HRDashboard() {
  const [metrics, setMetrics] = useState(null);
  const environment =
    window.location.hostname === "localhost" ? "development" : "production";
  const apiUrl = config[environment].apiUrl;
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState({});
  const [userRoles, setUserRoles] = useState([]);

  const account = useSelector((state) => state.account.user);
  const userId = account.id;

  // console.log(userId);

  useEffect(() => {
    setLoading(true);
    const fetchUserRoles = async () => {
      try {
        const response = await api.get(`/hr/dashboard`);
        const data = response.data;
        setMetrics(data);
      } catch (error) {
} finally {
        setLoading(false); // ✅ Done loading whether success or error
      }
    };

    fetchUserRoles();
  }, []);

  const monthLabels = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const statuses = ["paid", "pending", "partially_paid"];
  const statusColors = {
    paid: "rgba(75, 192, 192, 0.6)",
    pending: "rgba(255, 99, 132, 0.6)",
    partially_paid: "rgba(255, 206, 86, 0.6)",
  };

  const labels = Array.from(
    { length: new Date().getMonth() + 1 },
    (_, i) => monthLabels[i],
  );

  const datasets = statuses?.map((status) => ({
    label: status,
    backgroundColor: statusColors[status],
    data: labels.map((_, i) => {
      const monthIndex = i + 1;
      const entry = metrics?.payrollSummary?.find(
        (e) => e.month === monthIndex && e.payment_status === status,
      );
      return entry ? Number(entry.total) : 0;
    }),
  }));

  useEffect(() => {
    axios
      .get(`/dashboard/events-summary`, {
        params: { userId },
      })
      .then((res) => {
        setSummary(res.data.summary);
      });
  }, []);

  useEffect(() => {
    const fetchUserRoles = async () => {
      try {
        const response = await api.get(`/positions`);
        const rolesData = response.data;
setUserRoles(rolesData);
      } catch (error) {
}
    };

    fetchUserRoles();
  }, [apiUrl]);

  return (
    <React.Fragment>
      <Row>
        {metrics ? (
          <>
            <Col>
              <MetricCard
                title="Total Employees"
                value={metrics.totalEmployees}
                icon="👥"
              />
            </Col>
            <Col>
              <MetricCard
                title="Present Today"
                value={`${metrics.presentToday}/${metrics.totalEmployees}`}
                icon="✅"
              />
            </Col>
            <Col>
              <MetricCard
                title="Pending Leave"
                value={metrics.pendingLeave}
                icon="📝"
              />
            </Col>
            <Col>
              <MetricCard
                title="Avg Tenure"
                value={`${metrics?.avgTenure?.toFixed(1)} years`}
                icon="⏳"
              />
            </Col>
          </>
        ) : (
          <div>Loading metrics...</div>
        )}
      </Row>

      <Row>
        <Col md={12}>
          <MainCard title="Payroll Summary" isOption>
            {metrics && (
              <Bar
                data={{
                  labels,
                  datasets,
                }}
                options={{
                  responsive: true,
                  plugins: {
                    title: {
                      display: true,
                      text: "Payroll Summary by Payment Status",
                    },
                    legend: {
                      position: "top",
                    },
                  },
                }}
              />
            )}
          </MainCard>
        </Col>
      </Row>
      <Row>
        <Col lg={4}>
          <Card className="mb-4">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Latest Events</h5>
              <Badge bg="primary">{Object.values(summary).flat().length}</Badge>
            </Card.Header>

            <Card.Body>
              <div className="events-list">
                {summary &&
                  Object.entries(summary)?.map(([status, event, key]) => (
                    <ul className="space-y-3" key={key}>
                      <span className="text-capitalize">{`${status} ${event.eventType}`}</span>
                      <li>
                        <div className="font-semibold d-flex justify-content-between">
                          <h6>{event?.title}</h6>
                          <div className="text-sm text-muted">
                            <small>
                              {new Date(event.fromDate).toLocaleDateString()} →{" "}
                              {new Date(event.toDate).toLocaleDateString()}
                            </small>
                          </div>
                        </div>
                      </li>
                    </ul>
                  ))}
              </div>

              <Button variant="outline-primary" className="w-100 mt-3">
                <Link to={`/calendar/events`}>View All Events</Link>
              </Button>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <MainCard title="Department Distribution">
            {metrics && (
              <Pie
                data={{
                  labels: metrics?.departments?.map((dept) => {
                    const foundDept = userRoles.find(
                      (o) =>
                        Number(o.department_id) === Number(dept.department),
                    );
                    return foundDept?.department_name || "—";
                  }),
                  datasets: [
                    {
                      data: metrics?.departments?.map((dept) => dept.count),
                      backgroundColor: [
                        "#FF6384",
                        "#36A2EB",
                        "#FFCE56",
                        "#4BC0C0",
                        "#9966FF",
                        "#FF9F40",
                        "#8AC926",
                        "#1982C4",
                      ],
                    },
                  ],
                }}
              />
            )}
          </MainCard>
        </Col>
        <Col md={4}>
          <MainCard title="Gender Distribution">
            {metrics && (
              <Pie
                data={{
                  labels: metrics?.genderDistribution?.map((g) => g.gender),
                  datasets: [
                    {
                      data: metrics?.genderDistribution?.map((g) => g.count),
                      backgroundColor: ["#36A2EB", "#FF6384", "#FFCE56"],
                    },
                  ],
                }}
              />
            )}
          </MainCard>
        </Col>
      </Row>
    </React.Fragment>
  );
}

const MetricCard = ({ title, value, icon }) => (
  <Card>
    <Card.Body>
      <div>{title}</div>
      <div className="text-4xl mr-4 justqify-content-between">
        {icon}
        {value}
      </div>
    </Card.Body>
  </Card>
);

// const EventItem = ({ title, date }) => {
//   const eventDate = new Date(date);
//   const now = new Date();
//   const daysLeft = Math.ceil((eventDate - now) / (1000 * 60 * 60 * 24));

//   return (
//     <li className="flex justify-between items-center border-b pb-2">
//       <div>
//         <h3 className="font-medium">{title}</h3>
//         <p className="text-sm text-gray-500">{eventDate?.toDateString()}</p>
//       </div>
//       <span
//         className={`px-3 py-1 rounded-full text-sm ${
//           daysLeft < 7 ? "bg-red-100 text-red-800" : "bg-blue-100 text-blue-800"
//         }`}
//       >
//         {daysLeft > 0 ? `${daysLeft} days` : "Today"}
//       </span>
//     </li>
//   );
// };

const EventItem = ({ title, date }) => {
  const eventDate = new Date(date);
  const now = new Date();
  const daysLeft = Math.ceil((eventDate - now) / (1000 * 60 * 60 * 24));

  return (
    <li className="flex justify-between items-center border-b pb-2">
      <div>
        <h3 className="font-medium">{title}</h3>
        <p className="text-sm text-gray-500">{eventDate?.toDateString()}</p>
      </div>
      <span
        className={`px-3 py-1 rounded-full text-sm ${
          daysLeft < 7 ? "bg-red-100 text-red-800" : "bg-blue-100 text-blue-800"
        }`}
      >
        {daysLeft > 0 ? `${daysLeft} days` : "Today"}
      </span>
    </li>
  );
};
